import type {Ref, ComponentPropsWithoutRef, ComponentType} from 'react';
import React from 'react';

import {FloatingMenu, FloatingMenuBody} from '@pb/kit-component-menu';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './listbox.component.module.styl';

import type {FloatingMenuProps} from '@pb/kit-component-menu';


const classNames = withCssModule(styles);

type _FloatingMenuProps = Omit<FloatingMenuProps, 'onChange'>;

export interface ListboxProps extends _FloatingMenuProps {
    /**
     * Get it from useListboxFloating
     **/
    floatingElRef: Ref<HTMLDivElement>,

    /**
     * Custom body component, if you want to override default scrollable body
     */
    bodyComponent?: ComponentType<ComponentPropsWithoutRef<'div'>> | string,
}

/**
 * Listbox
 *
 * Displays Floating Listbox with Items next an anchor element. Use it in case the ready made components e.g. `DropdownMenu`, `Combobox` or `Autosuggest` do not meet your requirements.
 *
 * **Features**
 *
 * * Floating Listbox with Items
 * * Keyboard navigation
 * * A11y support
 *
 * **Accessibility**
 *
 * All components in this package are designed to be accessible. It implements WAI pattern Combobox and Listbox. Your anchor element should be interactive element. It's role is "combobox" by default.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-listbox--documentation}
 * */
export function Listbox(props: ListboxProps) {
    const {
        children,
        floatingElRef,
        bodyComponent: BodyComponent = FloatingMenuBody,
        ...floatingMenuProps
    } = props;

    const flip = floatingMenuProps.getContext().placement.startsWith('top-');

    return (
        <FloatingMenu
            {...floatingMenuProps}
            className={classNames(floatingMenuProps.className, {
                floating_mix: !flip,
                floating_mix_flipped: flip,
            })}
            floatingFocusManagerProps={{
                ...floatingMenuProps.floatingFocusManagerProps,
                modal: false,
                initialFocus: -1,
            }}
            flip={flip}
            ref={floatingElRef}>
            <BodyComponent>
                {children}
            </BodyComponent>
        </FloatingMenu>
    );
}
